import { css } from '@emotion/css';
import { Box } from '@spark-web/box';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import Actions from './Actions';

type Props = {
  onClose: () => void;
  setValue: (value: string) => void;
  watch: (name: string) => string;
};

function DirectDebitAgreement({ onClose, setValue, watch }: Props) {
  return (
    <Box
      className={css({
        width: '80%',
        height: '100vh',
        display: 'flex',
        alignSelf: 'center',
      })}
    >
      <Stack gap="xxlarge" marginX="medium">
        <Text>
          This is your Direct Debit Service Agreement with Brighte Capital Pty Limited, User Identification Number
          502055/502056, (ABN 74 609 165 906). It explains what your obligations are when undertaking a Direct Debit
          arrangement with us. It also details what our obligations are to you as a Debit User.
        </Text>
        <Text>
          This agreement forms part of the terms and conditions of your Direct Debit Request (DDR) and should be read in
          conjunction with your DDR authorisation.
        </Text>
        <Text weight="semibold">Definitions</Text>
        <Text>
          <Text weight="semibold">Account</Text> means the account held at your financial institution from which we are
          authorised to arrange for funds to be debited and being the account nominated in your DDR.
        </Text>
        <Text>
          <Text weight="semibold">Agreement</Text> means this Direct Debit Request Service Agreement between you and us.
        </Text>
        <Text>
          <Text weight="semibold">Banking day</Text> means a day other than a Saturday or a Sunday or a public holiday
          listed throughout Australia.
        </Text>
        <Text>
          <Text weight="semibold">Debit day</Text> means the day that payment by you to us is due.
        </Text>
        <Text>
          <Text weight="semibold">Debit payment</Text> means a particular transaction where a debit is made.
        </Text>
        <Text>
          <Text weight="semibold">Direct Debit Request</Text> or DDR means the Direct Debit Request between us and you.
        </Text>
        <Text>
          <Text weight="semibold" inline>
            Us
          </Text>{' '}
          or{' '}
          <Text weight="semibold" inline>
            we
          </Text>{' '}
          means{' '}
          <Text weight="semibold" inline>
            Brighte Capital Pty Ltd
          </Text>{' '}
          (the Debit User) you have authorised by by your Direct Debit Request.
        </Text>
        <Text>
          <Text weight="semibold">You</Text> means the customer who has signed or authorised by other means the Direct
          Debit Request.
        </Text>
        <Text>
          <Text weight="semibold">Your financial institution</Text> means the financial institution nominated by you on
          the DDR at which the Account is maintained.
        </Text>
        <Text weight="semibold">1. Debiting your account</Text>
        <Text>
          1.1 By signing a Direct Debit Request and/or by agreeing to the terms of this agreement, you have authorised
          us to arrange for funds to be debited from your Account in accordance with clause 1.2.
        </Text>
        <Text>
          1.2 You request and authorise us to arrange, through our own financial institution, a debit to your Account of
          any amount payable by you to us under your loan agreement. This debit or charge will be made through the Bulk
          Electronic Clearing System (BECS) from your Account. We will only arrange for an amount that is payable by you
          to us under a loan agreement to be debited from your Account.
        </Text>
        <Text>
          1.3 If the debit day falls on a day that is not a banking day, we may direct your financial institution to
          debit your Account on the following banking day. If you are unsure about which day your Account has or will be
          debited you should ask your financial institution.
        </Text>
        <Text>
          1.4 The Direct Debit Request and this agreement contain the terms of the arrangement between us and you.
        </Text>
        <Text weight="semibold">2. Amendments by us</Text>
        <Text>
          2.1 We may vary any details of this agreement or a Direct Debit Request at any time by giving you at least
          fourteen{' '}
          <Text weight="semibold" inline>
            (14) days
          </Text>{' '}
          written notice.
        </Text>
        <Text weight="semibold">3. Amendments by you</Text>
        <Text>
          You may change*, stop or defer a debit payment, or terminate a Direct Debit Request by providing us with at
          least{' '}
          <Text weight="semibold" inline>
            3 days
          </Text>{' '}
          notification by writing to:
        </Text>
        <Text weight="semibold" inline>
          info@brighte.com.au
        </Text>{' '}
        <Text>or</Text>
        <Text>by telephoning us on 1300 27 4448 during business hours;</Text>
        <Text>or</Text>
        <Text>
          arranging it through your own financial institution, which is required to act promptly on your instructions.
        </Text>
        <Text>
          *Note: in relation to the above reference to ‘change’, your financial institution may ‘change’ your debit
          payment only to the extent of advising Brighte of your new account details.
        </Text>
        <Text weight="semibold">4. Your obligations</Text>
        <Text>
          4.1 It is your responsibility to ensure that there are sufficient clear funds available in your Account to
          allow a debit payment to be made in accordance with the Direct Debit Request.
        </Text>
        <Text>4.2 If there are insufficient clear funds in your Account to meet a debit payment:</Text>
        <Text>(a) you may be charged a fee and/or interest by your financial institution;</Text>
        <Text>(b) you may also incur fees or charges imposed or incurred by us; and</Text>
        <Text>
          (c) you must arrange for the debit payment to be made by another method or arrange for sufficient clear funds
          to be in your Account by an agreed time so that we can process the debit payment.
        </Text>
        <Text>
          4.3 You should check your Account statement to verify that the amounts debited from your Account are correct
        </Text>
        <Text weight="semibold">5. Dispute</Text>
        <Text>
          5.1 If you believe that there has been an error in debiting your Account, you should notify us directly on
          1300 27 4448 and confirm that notice in writing with us as soon as possible so that we can resolve your query
          more quickly. Alternatively you can take it up directly with your financial institution.
        </Text>
        <Text>
          5.2 If we conclude as a result of our investigations that your Account has been incorrectly debited we will
          respond to your query by arranging for your financial institution to adjust your Account (including interest
          and charges) accordingly. We will also notify you in writing of the amount by which your Account has been
          adjusted.
        </Text>
        <Text>
          5.3 If we conclude as a result of our investigations that your Account has not been incorrectly debited we
          will respond to your query by providing you with reasons and any evidence for this finding in writing.
        </Text>
        <Text weight="semibold">6. Accounts</Text>
        <Text>You should check:</Text>
        <Text>
          (a) with your financial institution whether direct debiting is available from your Account as direct debiting
          is not available on all accounts offered by financial institutions;
        </Text>
        <Text>
          (b) your Account details which you have provided to us are correct by checking them against a recent account
          statement; and
        </Text>
        <Text>
          (c) with your financial institution before completing the Direct Debit Request if you have any queries about
          how to complete the Direct Debit Request.
        </Text>
        <Text weight="semibold">7. Confidentiality</Text>
        <Text>
          7.1 We will keep any information (including your Account details) in your Direct Debit Request confidential.
          We will make reasonable efforts to keep any such information that we have about you secure and to ensure that
          any of our employees or agents who have access to information about you do not make any unauthorised use,
          modification, reproduction or disclosure of that information.
        </Text>
        <Text>7.2 We will only disclose information that we have about you:</Text>
        <Text>(a) to the extent specifically required by law; or</Text>
        <Text>
          (b) for the purposes of this agreement (including disclosing information in connection with any query or
          claim).
        </Text>
        <Text weight="semibold">8. Notice</Text>
        <Text>
          8.1 If you wish to notify us in writing about anything relating to this agreement, you should write to
        </Text>
        <Text>info@brighte.com.au</Text>
        <Text>8.2 We will notify you by sending a notice to the email address you have given us.</Text>
        <Text>
          8.3 Any notice will be deemed to have been received on when the machine or system from which the notice was
          sent indicates that it has been successfully transmitted or sent.
        </Text>
        <Actions onClose={onClose} setValue={setValue} watch={watch} />
      </Stack>
    </Box>
  );
}

export default DirectDebitAgreement;
